import { useState } from 'react';
import { HighlightedCode } from '../HighlightedCode';

/**
 * Render the spec files the RED phase authored as an expandable list.
 *
 * Each row is the file path + a click-to-expand to read the test body.
 * Mirrors the look of `RunTestsResultsList` so users get a consistent
 * "click row → see details" pattern across writeTests, runTests, and
 * tool-call inspection. The body renders through `HighlightedCode`
 * (same syntax highlighting the rest of the panel uses).
 */
export function TestWritesList({
  writes,
}: {
  writes: Array<{ path: string; content: string }>;
}) {
  return (
    <div
      className="pipeline-test-writes-list"
      data-testid="pipeline-test-writes-list"
    >
      {writes.map((w) => (
        <TestWriteRow key={w.path} path={w.path} content={w.content} />
      ))}
    </div>
  );
}

function TestWriteRow({
  path,
  content,
}: {
  path: string;
  content: string;
}) {
  const [open, setOpen] = useState(false);
  // Roughly count how many `it(` / `test(` blocks are in the file so
  // the user gets a quick read on what the file covers without having
  // to expand every single row.
  const testCount = (content.match(/\b(?:it|test)\s*\(/g) ?? []).length;
  const lineCount = content.split('\n').length;

  return (
    <div className="pipeline-test-write-row" data-testid="pipeline-test-write-row">
      <div
        className="pipeline-test-write-header"
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer' }}
      >
        <span className="step-expand-icon">{open ? '▾' : '▸'}</span>
        <span className="pipeline-test-write-path">{path}</span>
        <span className="pipeline-test-write-meta">
          {testCount > 0 ? `${testCount} test${testCount !== 1 ? 's' : ''}` : 'spec'}
          {' · '}
          {lineCount} line{lineCount !== 1 ? 's' : ''}
        </span>
      </div>
      {open && (
        <div className="pipeline-test-write-body">
          <HighlightedCode code={content} language="typescript" />
        </div>
      )}
    </div>
  );
}
